// "Did you find what you were looking for?" (#437). The markup is
// templates/_components/feedback.twig: a small floating box, bottom right, kept in a
// <template> with no form in it. This clones it into the page and makes it behave like a
// survey popup. JS only, on purpose (#460): a form in the page's HTML is what spam tools
// find and fill in.
//
// - It doesn't ask on arrival. Someone who has just landed hasn't had a chance to find
//   anything, so their answer is noise, and a popup on arrival is what gets dismissed
//   unread. It appears after REVEAL_AFTER_MS on the page, or once the visitor has scrolled
//   REVEAL_AT_SCROLL of the way down, whichever comes first.
// - On a phone it's a small "Feedback" tab first, which opens the box when tapped: the full
//   box would cover a fifth of the screen.
// - It shows only while the cookie banner isn't showing, so the two never stack, and steps
//   aside if "Cookie settings" brings the banner back.
// - While it shows, it claims space at the bottom of the page (bottom-space.js), so a
//   focused control is scrolled clear of it, never hidden behind it (WCAG 2.4.11).
// - It counts itself being shown and being closed, on the same anonymous tally as the
//   answers, so there's a response rate: without a denominator a satisfaction figure isn't
//   credible, and a rising close rate is the sign the box annoys people.
// - Yes/No is sent the moment it's tapped, so a No followed by closing the box still counts.
//   Yes goes by sendBeacon. No goes by fetch, because its reply carries the ticket the
//   optional second step (chips and comment) needs; the server only accepts that ticket a
//   few seconds after it was issued, which a person always is and a script isn't (#460).
// - Answering or closing (the X, or Escape) hides it for the rest of the session: one
//   boolean in sessionStorage, gone when the tab closes. Essential interface storage, like
//   remembering a dismissed notice, and listed in the cookie policy (#385).
// - Counts, never text, are mirrored to Matomo when `_paq` exists, which on the main site
//   means the visitor accepted cookies. No event ids, no comment text (#419).
//
// A no-op when the page has no box, as every module in this bundle must be.
import { onReady } from './dom.js';
import { beacon } from './analytics-beacons.js';
import { claimBottomSpace } from './bottom-space.js';

var ANSWER_URL = '/actions/_event-analytics/reporting/record-feedback';
var DETAIL_URL = '/actions/_event-analytics/reporting/record-feedback-detail';
var DONE = 'wohb-feedback-answered';
var REVEAL_AFTER_MS = 20000;
var REVEAL_AT_SCROLL = 0.5;
var THANKS_FOR_MS = 4000;
var GAP_PX = 16;
var PHONE = '(max-width: 639px)'; // Tailwind's `sm` breakpoint, below which the tab is used

function doneThisSession() {
    try { return window.sessionStorage.getItem(DONE) === '1'; } catch (e) { return false; }
}

function rememberDone() {
    try { window.sessionStorage.setItem(DONE, '1'); } catch (e) { /* private mode: ask again next page */ }
}

function mirror(action, name) {
    if (!window._paq) return;
    window._paq.push(name ? ['trackEvent', 'Feedback', action, name] : ['trackEvent', 'Feedback', action]);
}

onReady(function () {
    var template = document.querySelector('template[data-feedback-template]');
    if (!template || !template.content || doneThisSession()) return;

    // Cloned in where the template sits, which keeps it outside anything that gets hidden.
    template.parentNode.insertBefore(document.importNode(template.content, true), template.nextSibling);

    var box = document.querySelector('[data-feedback]');
    var placement = template.getAttribute('data-placement');
    var token = template.getAttribute('data-token');
    var ask = box.querySelector('[data-feedback-ask]');
    var detail = box.querySelector('[data-feedback-detail]');
    var status = box.querySelector('[data-feedback-status]');
    var title = box.querySelector('#feedback-title');
    var tab = document.querySelector('[data-feedback-open]');
    var banner = document.getElementById('cookie-consent');
    var phone = window.matchMedia ? window.matchMedia(PHONE) : { matches: false };

    var revealed = false;
    var open = false;       // on a phone, whether the tab has been tapped
    var answered = false;
    var closed = false;
    var counted = false;
    var ticket = '';        // from the No's reply; without it there's no second step to send

    function send(answer) {
        beacon(ANSWER_URL, { token: token, answer: answer });
    }

    function sendNo() {
        try {
            fetch(ANSWER_URL, {
                method: 'POST',
                body: new URLSearchParams({ token: token, answer: 'no' }),
                credentials: 'same-origin',
                keepalive: true
            })
                .then(function (r) { return r.json(); })
                .then(function (reply) { ticket = (reply && reply.ticket) || ''; })
                .catch(function () { /* counted or not, the page carries on */ });
        } catch (err) { /* never let feedback break the page */ }
    }

    function bannerShowing() {
        return !!banner && !banner.classList.contains('hidden');
    }

    function sync() {
        var offered = revealed && !closed && !bannerShowing();
        var collapsed = offered && phone.matches && !open;

        box.classList.toggle('hidden', !offered || collapsed);
        if (tab) {
            tab.classList.toggle('hidden', !collapsed);
            tab.setAttribute('aria-expanded', String(offered && !collapsed));
        }

        var showing = !offered ? null : collapsed ? tab : box;
        claimBottomSpace('feedback', showing ? showing.offsetHeight + GAP_PX * 2 : 0);

        // Counted once per page, the first time it's actually offered.
        if (offered && !counted) {
            counted = true;
            send('shown');
            mirror(placement + ': shown');
        }
    }

    function reveal() {
        if (revealed) return;
        revealed = true;
        window.removeEventListener('scroll', onScroll);
        sync();
    }

    function onScroll() {
        var scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable > 0 && window.scrollY / scrollable >= REVEAL_AT_SCROLL) reveal();
    }

    function close(byVisitor) {
        if (closed) return;
        closed = true;
        rememberDone();
        if (byVisitor && !answered) {
            send('closed');
            mirror(placement + ': closed');
        }
        sync();
    }

    function thanks() {
        answered = true;
        if (ask) ask.hidden = true;
        if (detail) detail.hidden = true;
        // The live region announces it and it's the visible confirmation; then the box goes.
        status.textContent = 'Thanks for letting us know.';
        sync();
        window.setTimeout(function () { close(false); }, THANKS_FOR_MS);
    }

    box.querySelector('[data-feedback-close]').addEventListener('click', function (e) {
        e.preventDefault();
        close(true);
    });

    box.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close(true);
    });

    if (tab) {
        tab.addEventListener('click', function () {
            open = true;
            sync();
            var first = box.querySelector('button[name="answer"]');
            if (first) first.focus();
        });
    }

    if (ask) {
        ask.addEventListener('click', function (e) {
            var button = e.target.closest('button[name="answer"]');
            if (!button) return;
            e.preventDefault();

            var answer = button.value;
            if (answer === 'no') sendNo(); else send(answer);
            mirror(placement + ': ' + answer);
            rememberDone();

            if (answer === 'yes' || !detail) {
                thanks();
                return;
            }
            answered = true; // a No is an answer; closing now isn't "closed without answering"
            ask.hidden = true;
            detail.hidden = false;
            title.textContent = 'What was missing?';
            sync(); // the box grew, so the claimed space must too
            // Move focus to the new question, so keyboard and screen-reader users land on it.
            var legend = detail.querySelector('legend[tabindex]');
            if (legend) legend.focus();
        });
    }

    if (detail) {
        detail.querySelector('[data-feedback-send]').addEventListener('click', function () {
            // Built by hand: there's no <form> to serialise (see the template).
            var data = new URLSearchParams();
            data.set('ticket', ticket);
            data.set('website', detail.querySelector('[name="website"]').value);
            data.set('comment', detail.querySelector('[name="comment"]').value);
            detail.querySelectorAll('[name="reasons[]"]:checked').forEach(function (input) {
                data.append('reasons[]', input.value);
            });

            try {
                fetch(DETAIL_URL, { method: 'POST', body: data, credentials: 'same-origin', keepalive: true })
                    .catch(function () { /* fire and forget, like the beacons */ });
            } catch (err) { /* never let feedback break the page */ }

            data.getAll('reasons[]').forEach(function (reason) {
                mirror(placement + ': no reason', reason);
            });
            thanks();
        });
    }

    // Follow the cookie banner: step aside while it shows, come back when it goes.
    if (banner && typeof MutationObserver !== 'undefined') {
        new MutationObserver(sync).observe(banner, { attributes: true, attributeFilter: ['class'] });
    }
    window.addEventListener('resize', sync);

    window.setTimeout(reveal, REVEAL_AFTER_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    sync();
});
